import { Alert, AlertDescription } from "@/components/podkit/alert/Alert";
import { Button } from "@/components/flexkit/Button";
import { useOrganization } from "@/queries/organization-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { type FC } from "react";
import { useNavigate } from "react-router-dom";

export type EnvironmentDeletionWarningProps = {
    environmentId: string;
};

export const EnvironmentDeletionWarning: FC<EnvironmentDeletionWarningProps> = ({ environmentId }) => {
    const { data: organization } = useOrganization();
    const { data: environment } = useEnvironment(environmentId);
    const navigate = useNavigate();

    // Only show warning for FREE_ONA and CORE tiers
    if (
        !organization ||
        (organization.tier !== OrganizationTier.FREE_ONA && organization.tier !== OrganizationTier.CORE)
    ) {
        return null;
    }

    // Only show warning when environment is stopping or stopped
    if (
        !environment ||
        (environment.status?.phase !== EnvironmentPhase.STOPPING &&
            environment.status?.phase !== EnvironmentPhase.STOPPED)
    ) {
        return null;
    }

    const isFreeTier = organization.tier === OrganizationTier.FREE_ONA;

    const handleUpgrade = () => {
        navigate("/settings/billing");
    };

    return (
        <Alert variant="default" className="border-border-muted bg-surface-02">
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                {isFreeTier && (
                    <>
                        <span className="text-sm text-content-secondary">
                            Environments are ephemeral so you dont have to manage clutter. Stopped ones auto-delete
                            after 3 days. Want more control over auto-deletion? Try our paid plan.
                        </span>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={handleUpgrade}
                            className="shrink-0 sm:ml-4"
                            data-tracking-id="view-plans-environment-deletion-warning"
                        >
                            View plans
                        </Button>
                    </>
                )}
                {!isFreeTier && (
                    <span className="text-sm text-content-secondary">
                        Environments are ephemeral so you don&apos;t have to manage clutter. Stopped ones auto-delete
                        after 7 days.
                    </span>
                )}
            </AlertDescription>
        </Alert>
    );
};
